import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "../interfaces/adsManagerGuidedEdit";
import type { MarketingCampaignBudgetSegment } from "../interfaces/KYDMarketing";
import type { Price } from "../interfaces/KYDTicket";
import { scheduleInputToUtcIso } from "./adsetSchedule";
import {
  snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid,
  snapUtcIsoInstantToMetaBudgetScheduleGrid,
  validateScheduledBudgetWithinAdFlight,
  type CampaignBudgetWindowsValidationError,
} from "./campaignBudgetMeta";
import { minorAmountToPrice } from "./minorAmountToPrice";

export type WallTimedBudgetSegmentLike = {
  time_start?: string | null;
  time_end?: string | null;
  amount?: { currency?: string | null } | null;
};

/** Params for {@link validateScheduledBudgetWallsWithinUtcFlight} (explicit export for callers). */
export type ValidateScheduledBudgetWallsWithinUtcFlightParams = {
  segments: WallTimedBudgetSegmentLike[];
  eventTimezone: string | undefined;
  flightStartIso: string;
  flightEndIso: string;
};

/**
 * Converts wall / wire segment times to UTC ISO and runs
 * {@link validateScheduledBudgetWithinAdFlight}. `amount` is a nominal placeholder — the flight
 * check uses `time_*` instants only (same pattern as create-flow Yup and Engage edit schema).
 */
export function validateScheduledBudgetWallsWithinUtcFlight(
  params: ValidateScheduledBudgetWallsWithinUtcFlightParams
): CampaignBudgetWindowsValidationError | null {
  const tz =
    params.eventTimezone?.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
  const segmentModels: MarketingCampaignBudgetSegment[] =
    params.segments.map((s, i, arr) => {
      const rawStart =
        scheduleInputToUtcIso(String(s.time_start ?? "").trim(), tz) ??
        String(s.time_start);
      const rawEnd =
        scheduleInputToUtcIso(String(s.time_end ?? "").trim(), tz) ??
        String(s.time_end);
      const isFirst = i === 0;
      const isLast = i === arr.length - 1;
      return {
        time_start: isFirst
          ? snapUtcIsoInstantToMetaBudgetScheduleGrid(rawStart, "start")
          : rawStart,
        time_end: isLast
          ? snapUtcIsoInstantToMetaBudgetScheduleGrid(rawEnd, "end")
          : rawEnd,
        amount: minorAmountToPrice(
          1,
          (s.amount?.currency ?? "USD") as Price["currency"]
        ),
      };
    });
  const flight = snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid({
    flightStartIso: params.flightStartIso,
    flightEndIso: params.flightEndIso,
  });
  return validateScheduledBudgetWithinAdFlight({
    segments: segmentModels,
    flightStartIso: flight.flightStartIso,
    flightEndIso: flight.flightEndIso,
  });
}

/** First phase index whose amount is missing, non-numeric, or not positive (create + Engage edit). */
export function getScheduledPhasePositiveAmountIssue(
  segments: Array<{ amount?: { amount?: unknown } | null } | null | undefined>
): { pathSuffix: string; message: string } | null {
  for (let i = 0; i < segments.length; i++) {
    const raw = segments[i]?.amount?.amount;
    const n =
      raw != null && String(raw).trim() !== ""
        ? Number(String(raw).trim())
        : NaN;
    if (!Number.isFinite(n) || n <= 0) {
      return {
        pathSuffix: `segments[${i}].amount.amount`,
        message: "Each phase needs a positive budget amount",
      };
    }
  }
  return null;
}
